import { useQuery, useQueryClient } from '@tanstack/react-query';
import { stockEntriesNewAPI } from '../../services/api';
import { StockEntryNew, StockEntryNewListResponse } from '../../types/stockEntryNew';

/**
 * Query hooks for Stock Entries (server-paginated list + deleted list).
 *
 * Key root MUST be 'stock-entries' — the slug the realtime-invalidation
 * middleware broadcasts for /api/stock-entries — so another user's change
 * auto-refreshes this list. invalidating the root key refreshes every
 * paginated/filtered variant and the deleted list at once.
 */
export interface StockEntriesListParams {
  skip: number;
  limit: number;
  search?: string;
  extra?: Record<string, any>;
}

export const stockEntriesKeys = {
  root: ['stock-entries'] as const,
  list: (params: StockEntriesListParams) => ['stock-entries', 'list', params] as const,
  deleted: ['stock-entries', 'deleted'] as const,
};

export function useStockEntriesList(params: StockEntriesListParams, enabled: boolean) {
  return useQuery<StockEntryNewListResponse>({
    queryKey: stockEntriesKeys.list(params),
    queryFn: () =>
      stockEntriesNewAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        undefined,
        undefined,
        undefined,
        params.extra,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedStockEntries(enabled: boolean) {
  return useQuery<StockEntryNew[]>({
    queryKey: stockEntriesKeys.deleted,
    queryFn: () => stockEntriesNewAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateStockEntries() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: stockEntriesKeys.root });
}
